import { useEffect, useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../../utils/api';
import { useCurrency } from '../../contexts/CurrencyContext';

/**
 * Debtors Aging Report — "Sundry Debtors — Age Wise".
 *
 * Buckets each unpaid (or partially-paid) invoice by the age of the
 * `invoice_date` and aggregates per customer:
 *   • Pending Bills (count)
 *   • Pending Total (sum of outstanding)
 *   • < 30 days · 30-60 · 60-120 · > 120 days
 *
 * Three actions:
 *   • Search box (client-side, filters by customer name)
 *   • Excel export (multi-row XLSX matching the on-screen table)
 *   • PDF / Print (letterhead-styled hidden iframe, A4 landscape — same
 *     idiom as the other Sales reports)
 *
 * Lives as a tab inside Sales Reports.
 */
const DebtorsAgingTab = () => {
  const { companySettings, formatAmount } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [asOf, setAsOf] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = asOf
          ? `/sales/reports/debtors-aging?as_of=${asOf}`
          : '/sales/reports/debtors-aging';
        const res = await api.get(url);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ rows: [], totals: {}, as_of: '' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [asOf]);

  // Live search — case-insensitive, customer name only.
  const visibleRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => (r.customer_name || '').toLowerCase().includes(q));
  }, [data, search]);

  const fmt = (n) => (parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });

  /* ── Excel export ───────────────────────────────────────────────── */
  const exportExcel = () => {
    if (!visibleRows.length) return alert('No rows to export.');
    const sheetRows = [
      ...visibleRows.map((r, i) => ({
        '#': i + 1,
        Customer: r.customer_name,
        'Pending Bills': r.pending_bills,
        'Pending Total': r.pending_total,
        '< 30 Days': r.b_30,
        '30-60 Days': r.b_30_60,
        '60-120 Days': r.b_60_120,
        '> 120 Days': r.b_120_plus,
        'Oldest Invoice': r.oldest_invoice_date,
      })),
      {
        '#': '', Customer: 'TOTALS',
        'Pending Bills': data.totals.pending_bills,
        'Pending Total': data.totals.pending_total,
        '< 30 Days': data.totals.b_30,
        '30-60 Days': data.totals.b_30_60,
        '60-120 Days': data.totals.b_60_120,
        '> 120 Days': data.totals.b_120_plus,
        'Oldest Invoice': '',
      },
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    XLSX.utils.book_append_sheet(wb, ws, 'Debtors Aging');
    XLSX.writeFile(wb, `debtors_aging_${data.as_of || new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── Letterhead-styled PDF (hidden iframe) ──────────────────────── */
  const exportPDF = () => {
    if (!visibleRows.length) return alert('No rows to export.');
    const bodyRows = visibleRows.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${(r.customer_name || '').replace(/</g, '&lt;')}</td>
        <td class="r">${r.pending_bills}</td>
        <td class="r">${fmt(r.pending_total)}</td>
        <td class="r">${fmt(r.b_30)}</td>
        <td class="r">${fmt(r.b_30_60)}</td>
        <td class="r">${fmt(r.b_60_120)}</td>
        <td class="r">${fmt(r.b_120_plus)}</td>
        <td>${r.oldest_invoice_date || ''}</td>
      </tr>`).join('');

    const totals = data.totals;
    const totalsRow = `
      <tr class="totals">
        <td colspan="2"><b>TOTALS</b></td>
        <td class="r"><b>${totals.pending_bills}</b></td>
        <td class="r"><b>${fmt(totals.pending_total)}</b></td>
        <td class="r"><b>${fmt(totals.b_30)}</b></td>
        <td class="r"><b>${fmt(totals.b_30_60)}</b></td>
        <td class="r"><b>${fmt(totals.b_60_120)}</b></td>
        <td class="r"><b>${fmt(totals.b_120_plus)}</b></td>
        <td></td>
      </tr>`;

    const headerHtml = `
      <div class="lh">
        ${companySettings?.logo_url ? `<img class="lh-logo" src="${companySettings.logo_url}" alt="" />` : ''}
        <div class="lh-meta">
          <div class="lh-name">${(companySettings?.name || '').replace(/</g, '&lt;')}</div>
          ${companySettings?.tax_id ? `<div class="lh-line">VAT: ${companySettings.tax_id}</div>` : ''}
        </div>
        ${companySettings?.document_header_url ? `<img class="lh-banner" src="${companySettings.document_header_url}" alt="" />` : ''}
      </div>`;

    const html = `
      <!doctype html><html><head><title>Debtors Aging — ${data.as_of || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; }
        .page { padding: 12mm 10mm; }
        .lh { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #0f766e; padding-bottom: 8px; margin-bottom: 10px; }
        .lh-logo { max-height: 56px; max-width: 130px; object-fit: contain; }
        .lh-banner { max-height: 56px; max-width: 240px; margin-left: auto; object-fit: contain; }
        .lh-name { font-size: 16pt; font-weight: 800; color: #0f172a; }
        .lh-line { font-size: 9pt; color: #64748b; }
        .doc-title { font-size: 14pt; font-weight: 700; color: #0f172a; margin: 6px 0; }
        .subline { font-size: 9pt; color: #475569; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th, td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; }
        th { background: #f1f5f9; font-size: 8pt; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; }
        td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        tr.totals td { background: #ecfeff; font-size: 10pt; }
        .meta { font-size: 8pt; color: #94a3b8; margin-top: 10px; text-align: right; }
        @media print { @page { size: A4 landscape; margin: 8mm; } }
      </style></head>
      <body>
        <div class="page">
          ${headerHtml}
          <div class="doc-title">Debtors Aging — Sundry Debtors (Age Wise)</div>
          <div class="subline">As of: <b>${data.as_of}</b></div>
          <table>
            <thead>
              <tr>
                <th>#</th><th>Customer</th>
                <th class="r">Pending Bills</th><th class="r">Pending Total</th>
                <th class="r">&lt; 30 days</th><th class="r">30-60 days</th>
                <th class="r">60-120 days</th><th class="r">&gt; 120 days</th>
                <th>Oldest Invoice</th>
              </tr>
            </thead>
            <tbody>${bodyRows}${totalsRow}</tbody>
          </table>
          <div class="meta">Generated ${new Date().toLocaleString()}</div>
        </div>
      </body></html>`;

    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, { position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0 });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => {
      const imgs = Array.from(doc.images || []);
      const waiters = imgs.map((img) => (img.complete && img.naturalWidth > 0)
        ? Promise.resolve()
        : new Promise((res) => {
            img.addEventListener('load', res, { once: true });
            img.addEventListener('error', res, { once: true });
            setTimeout(res, 3000);
          }));
      Promise.all(waiters).then(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* noop */ }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1500);
      });
    }, 200);
  };

  if (loading) return <div className="bg-white border rounded-lg p-6 text-center text-sm text-slate-500">Loading…</div>;
  if (!data) return <div className="bg-white border rounded-lg p-6 text-center text-sm text-slate-500">No data</div>;

  return (
    <div className="space-y-4" data-testid="debtors-aging-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 bg-white border rounded-lg p-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">As of</label>
            <input
              type="date"
              value={asOf || data.as_of || ''}
              onChange={(e) => setAsOf(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
              data-testid="da-as-of"
            />
          </div>
          <div className="min-w-[220px]">
            <label className="block text-xs font-semibold text-slate-700 mb-1">Search customer</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to filter…"
              className="border rounded px-2 py-1.5 text-sm w-full"
              data-testid="da-search"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportExcel} disabled={!visibleRows.length} className="px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-40" data-testid="da-excel">📊 Excel</button>
          <button onClick={exportPDF} disabled={!visibleRows.length} className="px-3 py-1.5 rounded border border-rose-600 text-rose-700 hover:bg-rose-50 text-sm disabled:opacity-40" data-testid="da-pdf">📄 PDF / Print</button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Pending Total" value={data.totals.pending_total} accent="rose" />
        <SummaryCard label="< 30 days" value={data.totals.b_30} accent="emerald" />
        <SummaryCard label="30-60 days" value={data.totals.b_30_60} accent="amber" />
        <SummaryCard label="60-120 days" value={data.totals.b_60_120} accent="orange" />
        <SummaryCard label="> 120 days" value={data.totals.b_120_plus} accent="red" />
      </div>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Customer</th>
              <th className="px-3 py-2 text-right">Pending Bills</th>
              <th className="px-3 py-2 text-right">Pending Total</th>
              <th className="px-3 py-2 text-right">&lt; 30 Days</th>
              <th className="px-3 py-2 text-right">30-60 Days</th>
              <th className="px-3 py-2 text-right">60-120 Days</th>
              <th className="px-3 py-2 text-right">&gt; 120 Days</th>
              <th className="px-3 py-2 text-left">Oldest Invoice</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) => (
              <tr key={r.customer_id || i} className="border-t hover:bg-slate-50" data-testid={`da-row-${r.customer_id || i}`}>
                <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                <td className="px-3 py-2 font-medium">
                  {r.customer_name}
                  {(r.unapplied_advance || 0) > 0 && (
                    <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700" title={`Net of ${fmt(r.unapplied_advance)} unapplied advance`}>
                      −{fmt(r.unapplied_advance)} adv
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{r.pending_bills}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(r.pending_total)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.b_30 ? fmt(r.b_30) : '-'}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{r.b_30_60 ? fmt(r.b_30_60) : '-'}</td>
                <td className="px-3 py-2 text-right font-mono text-orange-700">{r.b_60_120 ? fmt(r.b_60_120) : '-'}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700">{r.b_120_plus ? fmt(r.b_120_plus) : '-'}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.oldest_invoice_date || '-'}</td>
              </tr>
            ))}
            {!visibleRows.length && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">No outstanding debtors.</td></tr>
            )}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="border-t bg-cyan-50 font-bold">
                <td className="px-3 py-2" colSpan={2}>TOTALS</td>
                <td className="px-3 py-2 text-right">{data.totals.pending_bills}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(data.totals.pending_total)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(data.totals.b_30)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(data.totals.b_30_60)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(data.totals.b_60_120)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(data.totals.b_120_plus)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="text-xs text-slate-500">
        Showing {visibleRows.length} of {data.rows.length} debtor{data.rows.length === 1 ? '' : 's'} · Company: {companySettings?.name || '—'} · Aging based on invoice_date · As of {data.as_of}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, accent }) => {
  const accentMap = {
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    red: 'bg-red-50 border-red-300 text-red-900',
  };
  return (
    <div className={`border rounded-lg p-3 ${accentMap[accent] || ''}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-mono font-bold">{(parseFloat(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</div>
    </div>
  );
};

export default DebtorsAgingTab;
